import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormFields } from '../../../../../components/UpsertInsuranceModal/UpsertInsuranceModal.meta';
import {
  HtmlTable,
  HtmlTableProps,
} from '../../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Text } from '../../../../../../../CommonComponents/OPL/Text/Text';
import { BeneficiaryType } from '../../../../../../PersonalInfo/api/useTrustsQuery';
import { Select } from '../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { Input } from '../../../../../../../CommonComponents/OPL/Input/Input';
import { IconButton } from '../../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { Button } from '../../../../../../../CommonComponents/OPL/Button/Button';
import { BeneficiaryDTO } from '../../../../../../../Profile/Accounts/api/useAccountsQuery';

interface Props extends HtmlTableProps {
  type: BeneficiaryType;
  beneficiaries: (BeneficiaryDTO & { key: string })[];
  options: { label: string; value: string | null; isVisible: boolean }[];
  onAppend(): void;
  onRemove(index: number): void;
  onUpdateOptions: React.Dispatch<
    React.SetStateAction<
      {
        label: string;
        value: string | null;
        isVisible: boolean;
      }[]
    >
  >;
  isReadOnly: boolean;
}

export const BeneficiariesTable = ({
  type,
  beneficiaries,
  options,
  onAppend,
  onRemove,
  onUpdateOptions,
  isReadOnly,
  ...rest
}: Props) => {
  const { control, register, setValue } = useFormContext<FormFields>();

  const total = beneficiaries
    .filter(beneficiary => beneficiary.Type === type)
    .reduce((acc, b) => +b.Percentage + acc, 0);

  const visibleOnlyBeneficiaryOptions = options.filter(option => option.isVisible);

  return (
    <HtmlTable variant="embedded" columnSpacing="small" fixedHead {...rest}>
      <table>
        <colgroup>
          <col width="60%" />
          <col width="30%" />
          <col width="10%" />
        </colgroup>

        <thead>
          <tr>
            <th>
              <Text variant="smallCaps" textAlign="left">
                {type === BeneficiaryType.Primary ? 'Primary' : 'Contingent'}
              </Text>
            </th>
            <th colSpan={2}>
              <Text variant="smallCaps" textAlign="left">
                Allocation
              </Text>
            </th>
          </tr>
        </thead>
        <tbody>
          {beneficiaries.filter(({ Type }) => Type === type).length === 0 && isReadOnly && (
            <tr>
              <td>
                <Select disabled />
              </td>
              <td>
                <Input disabled rightIcon="far fa-percent" />
              </td>
              <td />
            </tr>
          )}

          {beneficiaries.map((beneficiary, index) => {
            if (beneficiary.Type !== type) {
              return null;
            }

            return (
              <tr key={beneficiary.key}>
                <td>
                  <Controller
                    key={beneficiary.key}
                    control={control}
                    name={`AccountBeneficiaries.Beneficiaries.${index}.Id`}
                    render={({ field: { value, onChange, ref } }) => (
                      <Select
                        isCreatable
                        disabled={isReadOnly}
                        innerRef={ref}
                        value={value && options.find(option => option.value === value)}
                        options={visibleOnlyBeneficiaryOptions}
                        onCreateOption={name => {
                          onUpdateOptions(state => {
                            return [...state, { label: name, value: '', isVisible: false }];
                          });

                          setValue(`AccountBeneficiaries.Beneficiaries.${index}.Name`, name);
                        }}
                        onChange={opt => {
                          if (opt) {
                            onChange(opt.value);

                            setValue(`AccountBeneficiaries.Beneficiaries.${index}.Name`, opt.label);

                            onUpdateOptions(state => {
                              return state.map(beneficiaryOption => {
                                if (opt?.value === beneficiaryOption.value) {
                                  return {
                                    ...beneficiaryOption,
                                    isVisible: false,
                                  };
                                }

                                return beneficiaryOption;
                              });
                            });
                          }
                        }}
                        isPortal
                      />
                    )}
                  />
                </td>
                <td>
                  <Input
                    key={beneficiary.key}
                    {...register(`AccountBeneficiaries.Beneficiaries.${index}.Percentage`)}
                    disabled={isReadOnly}
                    type="number"
                    placeholder="0"
                    rightIcon="far fa-percent"
                  />
                </td>
                {!isReadOnly && (
                  <td>
                    <IconButton
                      variant="subtleInactive"
                      icon="far fa-minus-circle"
                      onClick={() => onRemove(index)}
                    />
                  </td>
                )}
              </tr>
            );
          })}

          {!isReadOnly && (
            <tr>
              <td colSpan={3}>
                <Button variant="link" leftIcon="fas fa-plus" onClick={onAppend}>
                  Add a Beneficiary
                </Button>
              </td>
            </tr>
          )}

          <tr>
            <td>
              <Text fontWeight={600} textAlign="right">
                Total Allocation
              </Text>
            </td>
            <td colSpan={2}>
              <Text fontWeight={600}>{total}%</Text>
            </td>
          </tr>
        </tbody>
      </table>
    </HtmlTable>
  );
};
